import React from 'react';
import { PlusCircleIcon, AlertTriangleIcon, ShieldCheckIcon, CheckSquareIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
export function Screen15_NewInfo({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const handleApplyUpdates = () => {
    const updatedSteps = data.steps.map((step, i) => {
      // Step 5 (index 4): Lift beam — add wind hazard
      if (i === 4) {
        return {
          ...step,
          hazards: [...step.hazards, {
            hazard: 'Wind affecting load stability',
            controls: ['Stop lift if wind increases or load becomes unstable', 'Confirm radio communication between crane operator and signal person before lift'],
            riskLevel: 'HIGH' as const,
            isNew: true
          }]
        };
      }
      // Step 6 (index 5): Guide beam — add clearance control
      if (i === 5) {
        return {
          ...step,
          hazards: step.hazards.map((h, j) => {
            if (j === 1) {
              // Struck-by swinging load
              return {
                ...h,
                controls: [...h.controls, 'Verify all workers below the lift path are cleared before each lift']
              };
            }
            return h;
          })
        };
      }
      return step;
    });
    updateData({
      steps: updatedSteps,
      selectedHazards: [...data.selectedHazards, 'Wind affecting load stability'],
      engineeringControls: [...data.engineeringControls, 'Stop lift if wind increases or load becomes unstable'],
      adminControls: [...data.adminControls, 'Confirm radio communication between crane operator and signal person before lift'],
      safetyChecks: [...data.safetyChecks, 'Verify all workers below the lift path are cleared before each lift'],
      meetingScore: 72
    });
    onNext();
  };
  return <div className="flex flex-col h-full" data-id="element-616">
      <div className="mb-6" data-id="element-617">
        <h2 className="text-2xl font-bold text-white mb-2" data-id="element-618">
          New Information Detected
        </h2>
        <p className="text-slate-400" data-id="element-619">
          AI found information from the meeting that should be added to the PTP.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-620">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-2" data-id="element-621">
          Detected Additions
        </h3>

        {/* Addition 1 — New hazard on Step 5 */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 border-l-4 border-l-red-500" data-id="element-622">
          <div className="flex items-center gap-2 mb-1" data-id="element-623">
            <AlertTriangleIcon className="w-4 h-4 text-red-500" data-id="element-624" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-625">
              Hazard → Step 5: Lift beam
            </span>
          </div>
          <p className="text-white font-medium mb-2" data-id="element-626">
            Wind affecting load stability
          </p>
          <div className="bg-slate-950 rounded p-3" data-id="element-627">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-628">
              Reason
            </p>
            <p className="text-slate-300 text-sm italic" data-id="element-629">
              The foreman said the wind picks up near midday and the load may
              move during lifting.
            </p>
          </div>
        </div>

        {/* Addition 2 — Control for new hazard on Step 5 */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 border-l-4 border-l-green-500" data-id="element-630">
          <div className="flex items-center gap-2 mb-1" data-id="element-631">
            <ShieldCheckIcon className="w-4 h-4 text-green-500" data-id="element-632" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-633">
              Control → Step 5: Lift beam
            </span>
          </div>
          <p className="text-white font-medium mb-2" data-id="element-634">
            Stop lift if wind increases or load becomes unstable
          </p>
          <div className="bg-slate-950 rounded p-3" data-id="element-635">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-636">
              Reason
            </p>
            <p className="text-slate-300 text-sm italic" data-id="element-637">
              The crane operator stated the lift must stop immediately if wind
              conditions change or the beam starts rotating.
            </p>
          </div>
        </div>

        {/* Addition 3 — Control for new hazard on Step 5 */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 border-l-4 border-l-green-500" data-id="element-638">
          <div className="flex items-center gap-2 mb-1" data-id="element-639">
            <ShieldCheckIcon className="w-4 h-4 text-green-500" data-id="element-640" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-641">
              Control → Step 5: Lift beam
            </span>
          </div>
          <p className="text-white font-medium mb-2" data-id="element-642">
            Confirm radio communication between crane operator and signal person
            before lift
          </p>
          <div className="bg-slate-950 rounded p-3" data-id="element-643">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-644">
              Reason
            </p>
            <p className="text-slate-300 text-sm italic" data-id="element-645">
              The meeting included discussion about hand signals and radio
              backup.
            </p>
          </div>
        </div>

        {/* Addition 4 — Control added to Step 6 */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 border-l-4 border-l-blue-500" data-id="element-646">
          <div className="flex items-center gap-2 mb-1" data-id="element-647">
            <CheckSquareIcon className="w-4 h-4 text-blue-500" data-id="element-648" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-649">
              Control → Step 6: Guide beam
            </span>
          </div>
          <p className="text-white font-medium mb-2" data-id="element-650">
            Verify all workers below the lift path are cleared before each lift
          </p>
          <div className="bg-slate-950 rounded p-3" data-id="element-651">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-652">
              Reason
            </p>
            <p className="text-slate-300 text-sm italic" data-id="element-653">
              The foreman reminded the crew to keep the area under the suspended
              load clear.
            </p>
          </div>
        </div>

        {/* Change Summary Box */}
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mt-6 flex items-center justify-center gap-2" data-id="element-654">
          <PlusCircleIcon className="w-5 h-5 text-orange-500" data-id="element-655" />
          <span className="text-orange-400 font-bold" data-id="element-656">
            4 updates will be added to the PTP
          </span>
        </div>
      </div>

      <div className="pt-4 space-y-3 bg-slate-950" data-id="element-657">
        <PrimaryButton onClick={handleApplyUpdates} data-id="element-658">
          Apply Updates to PTP
        </PrimaryButton>
        <SecondaryButton onClick={() => {}} data-id="element-659">Dismiss</SecondaryButton>
      </div>
    </div>;
}